import { Cell, flexRender, Row } from '@tanstack/react-table';
import { cn, NoddiIcon } from 'noddi-ui-common';
import type { MouseEvent, ReactNode } from 'react';
import { Button } from '../../../../base/button/button';
import { ContextMenu, ContextMenuTrigger } from '../../../../base/context-menu';
import { TableCell, TableRow } from '../../../../base/table';
import { shouldSkipRowNavigation } from './shouldSkipRowNavigation';
import type { RowKeyboardProps } from './useRowKeyboardNavigation';

type Props<TData> = {
  row: Row<TData>;
  showRowMenu: boolean;
  hasRowSelection: boolean;
  rowClassName: string;
  /** Set on a row that opens or picks something, so a keyboard reaches and activates it too. */
  keyboardProps?: RowKeyboardProps;
  openMenuLabel: string;
  menuContent: ReactNode;
  onRowClick: (row: TData, event: MouseEvent) => void;
  onRowContextMenu?: (row: TData) => void;
  onMenuButtonClick: (e: MouseEvent) => void;
};

export function TableBodyRow<TData>({
  row,
  showRowMenu,
  hasRowSelection,
  rowClassName,
  keyboardProps,
  openMenuLabel,
  menuContent,
  onRowClick,
  onRowContextMenu,
  onMenuButtonClick
}: Props<TData>) {
  const isSelected = row.getIsSelected?.() ?? false;
  const dataState = isSelected ? 'selected' : undefined;

  // select-cell class is used in TableRow to avoid changing bg on hover where the checkbox is.
  // Selection tint comes from TableRow's data-[state=selected] styles (no ! important needed); the
  // checkbox-hover reset skips selected rows, or ticking a box hides its tint until the pointer leaves.
  const handleRowClick = (e: MouseEvent<HTMLTableRowElement>) => {
    if (
      shouldSkipRowNavigation({
        target: e.target,
        currentTarget: e.currentTarget,
        selection: window.getSelection()
      })
    ) {
      return;
    }
    onRowClick(row.original, e);
  };

  const tableRow = (
    <TableRow
      className={cn(
        rowClassName,
        hasRowSelection && '[&:not([data-state=selected]):has(.select-cell:hover)]:bg-transparent',
        // Keyboard focus only: a mouse click focuses the row too, without the outline.
        keyboardProps && 'focus-visible:outline-2 focus-visible:-outline-offset-2 focus-visible:outline-ring'
      )}
      data-state={dataState}
      {...keyboardProps}
      onClick={handleRowClick}
      onContextMenu={
        onRowContextMenu
          ? (event) => {
              event.preventDefault();
              onRowContextMenu(row.original);
            }
          : undefined
      }
    >
      {row.getVisibleCells().map((cell: Cell<TData, unknown>) => {
        const isSelectColumn = cell.column.id === 'select';
        return (
          <TableCell
            key={cell.id}
            onClick={isSelectColumn ? (e) => e.stopPropagation() : undefined}
            className={cn(isSelectColumn && 'select-cell cursor-default')}
            style={{
              width: cell.column.getSize(),
              minWidth: cell.column.columnDef.minSize,
              maxWidth: cell.column.columnDef.maxSize
            }}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </TableCell>
        );
      })}
      {showRowMenu && (
        <TableCell className='w-12 p-0' onClick={(e) => e.stopPropagation()}>
          <Button
            variant='ghost'
            className='size-8 p-0'
            onClick={(e) => {
              e.stopPropagation();
              onMenuButtonClick(e);
            }}
          >
            <span className='sr-only'>{openMenuLabel}</span>
            <NoddiIcon name='MoreHorizontal' />
          </Button>
        </TableCell>
      )}
    </TableRow>
  );

  if (!showRowMenu || !menuContent) {
    return tableRow;
  }

  return (
    <ContextMenu>
      <ContextMenuTrigger asChild>{tableRow}</ContextMenuTrigger>
      {menuContent}
    </ContextMenu>
  );
}
